/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomFormTextFieldV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender
 * @date : 28-06-2024
 * @description : Reusable and validated TextField component with custom styling.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { componentStyle } from "../../../../config/appTheme";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const regexMin3 = /^[a-zA-Z0-9._\- ]{3,}$/;
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const VALIDATORS = {
  text1: (trimmed) => (trimmed.length < 1 ? "1" : ""),
  text: (trimmed) => (trimmed.length < 3 ? "3" : ""),
  textR: (trimmed) => {
    if (trimmed.length < 3) return "3";
    if (!regexMin3.test(trimmed)) return "Invalid Format";
    return "";
  },
  textO: (trimmed) => (trimmed.length > 0 && trimmed.length < 3 ? "3" : ""),
  text4: (trimmed) => (trimmed.length < 4 ? "4" : ""),
  text5: (trimmed) => (trimmed.length < 5 ? "5" : ""),
  number: (trimmed) => (trimmed.length < 1 ? "1" : ""),  // Newly Added
};

const normalizeInputValue = (val) =>
  val.replace(/^\s+/, "").replace(/\s+$/, " ");

const validateInputByType = (fldType, trimmed) => {
  switch (fldType) {
    case "email":
      return !emailRegex.test(trimmed) ? "Invalid Email Address" : "";
    case "textR":
      return !regexMin3.test(trimmed) ? "Invalid Format" : "";
    default:
      return "";
  }
};

const validateTextCountByType = (fldType, trimmed) => {
  const validator = VALIDATORS[fldType];
  return validator ? validator(trimmed) : "";
};

const getMaxCharError = (maxLength, newValue) =>
  maxLength && newValue.length > maxLength ? maxLength : "";

const MinRequirementMessage = ({
  textCountValidation,
  length,
  fldType,
  type,
  countUnit,
}) => {
  if (!textCountValidation || textCountValidation === "Invalid Format")
    return null;

  const isMinRequired = length < 3 || fldType === "text5" || fldType === "number";
  const unitLabel =
    fldType === "number"
      ? "digit" + (Number(textCountValidation) > 1 ? "s" : "") + " "
      : (type === "number" ? "number" : countUnit) +
      (Number(textCountValidation) > 1 ? "s" : "") +
      " ";

  return (
    <Box sx={{ textAlign: "left" }}>
      <Typography
        sx={{
          mb: 0.5,
          ...componentStyle.font.form.textFieldError,
          fontWeight: 500,
          color: "#6f7174",
        }}
      >
        {isMinRequired ? "Minimum " : ""}
        <Typography
          component="span"
          sx={{
            color: "red",
            fontSize: "0.8rem",
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {textCountValidation}
        </Typography>{" "}
        {isMinRequired ? unitLabel : ""}
        {isMinRequired ? "required " : ""}
      </Typography>
    </Box>
  );
};

const MaxLengthMessage = ({ maxCharacterValidation, countUnit }) => {
  if (!maxCharacterValidation) return null;

  return (
    <Box sx={{ textAlign: "left" }}>
      <Typography
        sx={{
          mb: 0.5,
          ...componentStyle.font.form.textFieldError,
          fontWeight: 500,
          color: "#6f7174",
        }}
      >
        Maximum{" "}
        <Typography
          component="span"
          sx={{
            color: "red",
            fontSize: "0.8rem",
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {maxCharacterValidation}
        </Typography>{" "}
        {countUnit + (Number(maxCharacterValidation) > 1 ? "s" : "")} allowed
      </Typography>
    </Box>
  );
};

const InvalidFormatMessage = ({
  textCountValidation,
  length,
  type,
  countUnit,
}) => {
  if (textCountValidation !== "Invalid Format") return null;

  const isMinRequired = length < 3;
  const unitLabel =
    (type === "number" ? "number" : countUnit) +
    (Number(textCountValidation) > 1 ? "s" : "") +
    " ";

  return (
    <Box sx={{ textAlign: "left" }}>
      <Typography
        sx={{
          mb: 0.5,
          ...componentStyle.font.form.textFieldError,
          fontWeight: 500,
          color: "#6f7174",
        }}
      >
        {isMinRequired ? "Minimum " : ""}
        <Typography
          component="span"
          sx={{
            color: "red",
            fontSize: "0.8rem",
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {textCountValidation}
        </Typography>{" "}
        {isMinRequired ? unitLabel : ""}
        {isMinRequired ? "required " : ""}
      </Typography>
    </Box>
  );
};

const CustomFormTextFieldV2 = ({
  size = "small",
  label = "",
  type = "text",
  fldType = "text",
  placeholder = "",
  required = false,
  value = "",
  setValue = () => { },
  variant = "outlined",
  valueError = "",
  setValueError = () => { },
  inputProps = {},
  disabled = false,
  readOnly = false,
  isMultiline = false,
  minRows = 1,
  maxRows = 3,
  txtFldStyle = {},
  description = "",
  countUnit = "character", // unit label for min requirement message
  maxLength = null, // maximum character limit
  resetTrigger,
  wrapText = false,
}) => {
  /** ------------------------------
   * Validation Logic
   * ------------------------------ */
  const [textCountValidation, setTextCountValidation] = useState(null);
  const [maxCharacterValidation, setMaxCharacterValidation] = useState(null);
  const [length, setLength] = useState(0);

  useEffect(() => {
    if (resetTrigger === undefined) return;
    setTextCountValidation(null);
    setMaxCharacterValidation(null);
    setLength(0);
    setValueError("");
  }, [resetTrigger]);

  const validateTextCount = (val) => {
    const trimmed = val.trim();
    return validateTextCountByType(fldType, trimmed);
  };

  const handleChange = (e) => {
    let raw = e.target.value;
    if (type === "number") {
      const numericValue = Number(raw);
      setValue(numericValue);
      setValueError("");
      setTextCountValidation("");
      setMaxCharacterValidation("");
      return;
    }
    let newValue = normalizeInputValue(e.target.value);
    const trimmed = newValue.trim();
    const errorMsg = validateInputByType(fldType, trimmed);
    const textCountErrorMsg = validateTextCount(newValue);
    const maxCharErrorMsg = getMaxCharError(maxLength, newValue);
    setValueError(errorMsg);
    setTextCountValidation(textCountErrorMsg);
    if (maxLength && newValue.length > maxLength) {
      setMaxCharacterValidation(maxCharErrorMsg);
      return;
    } else {
      setMaxCharacterValidation(maxCharErrorMsg);
    }

    setValue(newValue);
    setLength(newValue.trim().length);
  };

  const textFieldStyle = {
    // HIDE DEFAULT NUMBER SPINNER
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& .MuiOutlinedInput-root": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d9d9d9",
        borderRadius: "0px",
      }, // Default
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#8c8c8c" }, // Hover
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#000000",
      }, // Focused
      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e0e0e0",
      }, // Disabled
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: "#e53935",
      }, // Error
      "& .MuiInputBase-input": {
        ...componentStyle.font.form.textFieldValue,

      },

      // 🔹 Change text color on hover
      "&:hover .MuiInputBase-input::placeholder": {
        color: "#848587", // dark text color on hover
      },

      // 🔹 Change text color on focus (selected)
      "&.Mui-focused .MuiInputBase-input::placeholder": {
        color: "#232323", // solid black when focused
      },
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      ...componentStyle.font.form.textFieldValue,
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
      ...componentStyle.font.form.textFieldValue,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      ...componentStyle.font.form.textFieldError,
      marginLeft: 0,
      marginTop: "4px",
    },
    ...txtFldStyle,
  };

  const InputProps = {
    readOnly,
    ...inputProps,
    sx: {
      backgroundColor: readOnly ? "#f5f5f5" : "#fff",
      ...(isMultiline ? {} : { height: "32px" }),
      pr: 0, // <-- remove right padding of the input box
      "&.Mui-disabled": {
        color: "#c0c0c0",
        WebkitTextFillColor: "#c0c0c0",
      },
      "& .MuiInputBase-input": {
        pr: 0, // <-- keep the text from pushing icon away
        ...(type == "number" && {
          paddingTop: 0,
          paddingBottom: 0,
        }),
      },

      "& .MuiInputAdornment-root": {
        m: 0,
        p: 0,
        height: "100%",
        maxHeight: "100%",
      },
    },
    endAdornment: type === "number" && !isMultiline && (
      <InputAdornment position="end" sx={{ m: 0, p: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            m: 0,
            p: 0,
          }}
        >
          {/* Increase Button */}
          <IconButton
            disableRipple
            size="small"
            sx={{
              m: 0,
              p: 0,
              width: "22px",
              height: "50%",
              borderRadius: 0,
              "&:hover": { backgroundColor: "#E5E5E5" },
            }}
            onClick={() =>
              handleChange({
                target: { value: Number(value || 0) + 1 },
              })
            }
          >
            <KeyboardArrowUpIcon sx={{ fontSize: "16px" }} />
          </IconButton>

          {/* Decrease Button */}
          <IconButton
            disableRipple
            size="small"
            sx={{
              m: 0,
              p: 0,
              width: "22px",
              height: "50%",
              borderRadius: 0,
              "&:hover": { backgroundColor: "#E5E5E5" },
            }}
            onClick={() =>
              handleChange({
                target: { value: Number(value || 0) - 1 },
              })
            }
          >
            <KeyboardArrowDownIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </Box>
      </InputAdornment>
    ),
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            ...componentStyle.font.form.textFieldLabel,
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}
      {Boolean(description) && (
        <Typography
          sx={{
            mb: 0.5,
            ...componentStyle.font.form.textFieldDescription,
            color: "#6f7174",
          }}
        >
          {description}
        </Typography>
      )}

      <MinRequirementMessage
        textCountValidation={textCountValidation}
        length={length}
        fldType={fldType}
        type={type}
        countUnit={countUnit}
      />

      <MaxLengthMessage
        maxCharacterValidation={maxCharacterValidation}
        countUnit={countUnit}
      />

      {/* TextField */}
      <TextField
        fullWidth
        size={size}
        type={type}
        variant={variant}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        error={Boolean(valueError)}
        helperText={valueError}
        multiline={isMultiline}
        minRows={minRows}
        maxRows={maxRows}
        disabled={disabled}
        InputProps={InputProps}
        sx={{
          ...textFieldStyle,
          ...(wrapText && {
            "& .MuiInputBase-input": {
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            },
          }),
        }}
      />
      <InvalidFormatMessage
        textCountValidation={textCountValidation}
        length={length}
        type={type}
        countUnit={countUnit}
      />
    </Box>
  );
};

export default CustomFormTextFieldV2;
